/**
 * API Client Service
 *
 * Handles HTTP requests to the backend API
 * Automatically includes authentication token from localStorage
 * SECURITY: Includes CSRF token in headers for all mutating requests
 */

import { csrfService } from './csrfService';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8008/api';
const TOKEN_KEY = 'fitbook_token';

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  body?: unknown;
  requiresAuth?: boolean;
}

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Make HTTP request to API
   */
  async request<T = unknown>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const {
      method = 'GET',
      headers = {},
      body = null,
      requiresAuth = false,
    } = options;

    // Build headers
    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...headers,
    };

    // Add authorization token if required or available
    const token = this.getToken();
    if (requiresAuth || token) {
      if (!token && requiresAuth) {
        throw new Error('Authentication required');
      }
      if (token) {
        requestHeaders['Authorization'] = `Bearer ${token}`;
      }
    }

    // SECURITY: Add CSRF token for state-changing requests
    // POST, PUT, DELETE, PATCH require CSRF protection
    if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
      try {
        const csrfToken = await csrfService.getToken();
        requestHeaders['X-CSRF-Token'] = csrfToken;
      } catch (error) {
        console.error('[ApiClient] Failed to get CSRF token:', error);
        // Continue without CSRF token if backend has it disabled
        // In production, this should throw an error
      }
    }

    // Build request config
    const config: RequestInit = {
      method,
      headers: requestHeaders,
      credentials: 'include', // Important: Include cookies for CSRF (session-based)
    };

    // Add body for non-GET requests
    if (body && method !== 'GET') {
      config.body = JSON.stringify(body);
    }

    try {
      const url = `${this.baseUrl}${endpoint}`;
      const response = await fetch(url, config);

      // Parse JSON response
      let data: ApiResponse<T>;
      try {
        data = await response.json();
      } catch (parseError) {
        // If JSON parsing fails, create error response
        data = {
          success: false,
          error: 'Error al parsear la respuesta del servidor',
        };
      }

      // Handle 401 Unauthorized - clear token and redirect to login
      if (response.status === 401) {
        this.clearToken();

        // Dispatch custom event for auth error (can be caught by AuthContext)
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));

        throw new Error(data.error || 'No autorizado');
      }

      // Handle 403 Forbidden - might be CSRF token issue
      if (response.status === 403 && data.error?.includes('CSRF')) {
        console.warn('[ApiClient] CSRF token validation failed, refreshing token...');

        // Refresh CSRF token and retry once
        try {
          await csrfService.refreshToken();
          // Note: We don't automatically retry here to avoid infinite loops
          // The user should retry the action manually
        } catch (refreshError) {
          console.error('[ApiClient] Failed to refresh CSRF token:', refreshError);
        }

        throw new Error(data.error || 'Error de validación de seguridad. Por favor, intenta de nuevo.');
      }

      // Handle other HTTP errors
      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return data;
    } catch (error) {
      // Network or other errors
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Error de conexión con el servidor');
    }
  }

  /**
   * GET request
   */
  async get<T = unknown>(endpoint: string, requiresAuth = false): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET', requiresAuth });
  }

  /**
   * POST request
   */
  async post<T = unknown>(
    endpoint: string,
    body: unknown,
    requiresAuth = false
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'POST', body, requiresAuth });
  }

  /**
   * PUT request
   */
  async put<T = unknown>(
    endpoint: string,
    body: unknown,
    requiresAuth = false
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'PUT', body, requiresAuth });
  }

  /**
   * DELETE request
   */
  async delete<T = unknown>(endpoint: string, requiresAuth = false): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE', requiresAuth });
  }

  /**
   * PATCH request
   */
  async patch<T = unknown>(
    endpoint: string,
    body: unknown,
    requiresAuth = false
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'PATCH', body, requiresAuth });
  }

  /**
   * Get stored token from localStorage
   */
  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  /**
   * Save token to localStorage
   */
  setToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
  }

  /**
   * Clear token from localStorage
   */
  clearToken(): void {
    localStorage.removeItem(TOKEN_KEY);
    // Also clear CSRF token
    csrfService.clearToken();
  }

  /**
   * Check if user is authenticated (has token)
   */
  isAuthenticated(): boolean {
    return this.getToken() !== null;
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Export types
export type { ApiResponse, RequestOptions };
